import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'

import clsx from 'clsx'
import { XIcon, IconButton } from '@buffer-mono/popcorn'

import { selectAIAssistantResponsive } from '~publish/legacy/ai/state/slice'
import { useAppSelector } from '~publish/legacy/store'

import styles from './IdeaComposerSidePanel.module.css'

export type SidePanelState = 'open' | 'closed'

/**
 * Sidepanel component for the IdeaComposer.
 * Used to render things like the AIAssistant.
 */
const IdeaComposerSidepanel = ({
  onClose,
  children,
  state,
  onAnimationEnd,
}: {
  onClose: () => void
  children: JSX.Element
  state?: SidePanelState
  onAnimationEnd?: () => void
}): JSX.Element => {
  const [animation, setAnimation] = useState<SidePanelState | ''>('')

  const AIAssistantResponsive = useAppSelector((reduxState) =>
    selectAIAssistantResponsive(reduxState),
  )

  const side = AIAssistantResponsive ? 'right' : 'left'

  const onAnimationEndCallback = useCallback(() => {
    onAnimationEnd?.()
  }, [onAnimationEnd])

  useEffect(() => {
    if (state) setAnimation(state)
    const timeoutId = setTimeout(onAnimationEndCallback, 300)
    return (): void => clearTimeout(timeoutId)
  }, [state, onAnimationEndCallback])

  return (
    <div
      data-testid="ideas-sidepanel"
      className={clsx(
        styles.wrapper,
        animation && styles[animation],
        styles[side],
      )}
    >
      <IconButton
        className={styles.closeButton}
        onClick={onClose}
        label="Close side panel"
        variant="tertiary"
        size="small"
        data-testid="close-button"
      >
        <XIcon />
      </IconButton>
      {children}
    </div>
  )
}

export default IdeaComposerSidepanel
